import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { apiFetch } from '../../lib/api';
import { inputClass, labelClass, traducirError, type NewsletterSettings, type Programacion } from './tipos';

/** Guarda un valor de ajuste (`PATCH /api/settings/:key`). */
async function guardarAjuste(key: string, value: unknown): Promise<void> {
  await apiFetch(`/api/settings/${key}`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
}

function BotonGuardar({ guardando, ok }: { guardando: boolean; ok: boolean }) {
  return (
    <div className="flex items-center justify-end gap-3">
      {ok && <span className="text-sm font-light text-emerald-700">Cambios guardados.</span>}
      <button
        type="submit"
        disabled={guardando}
        className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
      >
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  );
}

// ─────────────────────────────── Aprobador ─────────────────────────────────

function FormAprobador({ inicial }: { inicial: NewsletterSettings['approver'] }) {
  const [email, setEmail] = useState(inicial.email ?? '');
  const [name, setName] = useState(inicial.name ?? '');
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();
  const nameId = useId();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (guardando) return;
    setError(null);
    setOk(false);
    setGuardando(true);
    try {
      await guardarAjuste('approver', { email: email.trim(), name: name.trim() });
      setOk(true);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div>
        <h3 className="text-lg font-light text-azul-profundo">Aprobador</h3>
        <p className="mt-1 text-sm font-light text-gris">
          Recibe el correo para aprobar o rechazar cada edición.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={emailId} className={labelClass}>
            Correo del aprobador
          </label>
          <input
            id={emailId}
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setOk(false);
            }}
            placeholder="aprobador@ambec.cl"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={nameId} className={labelClass}>
            Nombre del aprobador
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setOk(false);
            }}
            placeholder="Nombre y apellido"
            className={inputClass}
          />
        </div>
      </div>

      <BotonGuardar guardando={guardando} ok={ok} />
    </form>
  );
}

// ─────────────────────────────── Remitente ─────────────────────────────────

function FormRemitente({ inicial }: { inicial: NewsletterSettings['sender'] }) {
  const [fromEmail, setFromEmail] = useState(inicial.from_email ?? '');
  const [fromName, setFromName] = useState(inicial.from_name ?? '');
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();
  const nameId = useId();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (guardando) return;
    setError(null);
    setOk(false);
    setGuardando(true);
    try {
      await guardarAjuste('sender', { from_email: fromEmail.trim(), from_name: fromName.trim() });
      setOk(true);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div>
        <h3 className="text-lg font-light text-azul-profundo">Remitente</h3>
        <p className="mt-1 text-sm font-light text-gris">
          Dirección y nombre desde los que se envía el newsletter a los clientes.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={emailId} className={labelClass}>
            Correo remitente
          </label>
          <input
            id={emailId}
            type="email"
            required
            value={fromEmail}
            onChange={(e) => {
              setFromEmail(e.target.value);
              setOk(false);
            }}
            placeholder="newsletter@ambec.cl"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={nameId} className={labelClass}>
            Nombre remitente
          </label>
          <input
            id={nameId}
            type="text"
            value={fromName}
            onChange={(e) => {
              setFromName(e.target.value);
              setOk(false);
            }}
            placeholder="AMBEC"
            className={inputClass}
          />
        </div>
      </div>

      <BotonGuardar guardando={guardando} ok={ok} />
    </form>
  );
}

// ─────────────────────────── TTL de aprobación ─────────────────────────────

function FormTtl({ inicial }: { inicial: number }) {
  const [horas, setHoras] = useState(String(inicial ?? ''));
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (guardando) return;
    setError(null);
    setOk(false);

    const valor = Number(horas);
    if (!Number.isFinite(valor) || valor <= 0) {
      setError('Ingresa un número de horas válido.');
      return;
    }

    setGuardando(true);
    try {
      await guardarAjuste('approval_token_ttl_hours', valor);
      setOk(true);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div>
        <h3 className="text-lg font-light text-azul-profundo">Vigencia del enlace de aprobación</h3>
        <p className="mt-1 text-sm font-light text-gris">
          Horas durante las que el enlace de aprobación del correo sigue siendo válido.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div className="max-w-xs">
        <label htmlFor={id} className={labelClass}>
          Horas de vigencia
        </label>
        <input
          id={id}
          type="number"
          min="1"
          step="1"
          value={horas}
          onChange={(e) => {
            setHoras(e.target.value);
            setOk(false);
          }}
          className={inputClass}
        />
      </div>

      <BotonGuardar guardando={guardando} ok={ok} />
    </form>
  );
}

// ─────────────────────────── Flags (solo lectura) ──────────────────────────

const FLAGS: { key: keyof NewsletterSettings; label: string; descripcion: string }[] = [
  {
    key: 'feature_segmentation_enabled',
    label: 'Segmentación por sector en el envío',
    descripcion: 'Enviar contenidos distintos según el sector de cada cliente.',
  },
  {
    key: 'feature_minisite_enabled',
    label: 'Minisitio de ediciones',
    descripcion: 'Publicar cada edición como página web navegable.',
  },
  {
    key: 'auto_send',
    label: 'Envío automático',
    descripcion: 'Enviar automáticamente al aprobar, sin confirmación manual.',
  },
];

function SeccionFlags({ settings }: { settings: NewsletterSettings }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-lg font-light text-azul-profundo">Funcionalidades futuras</h3>
        <p className="mt-1 text-sm font-light text-gris">
          Estas capacidades se activarán en fases futuras. Hoy son de solo lectura.
        </p>
      </div>
      <ul className="mt-5 flex flex-col divide-y divide-slate-100">
        {FLAGS.map((f) => {
          const activo = Boolean(settings[f.key]);
          return (
            <li key={f.key} className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <p className="text-sm font-normal text-texto">{f.label}</p>
                <p className="text-xs font-light text-gris">{f.descripcion}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500">
                  {activo ? 'Activado' : 'Desactivado'}
                </span>
                <span className="rounded-full border border-celeste bg-celeste/25 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-azul-profundo">
                  Roadmap · Requiere habilitación
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ──────────────────────────────── Sección ──────────────────────────────────


// ───────────────────────── Programación de Víctor ──────────────────────────

const fmtProxima = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('es-CL', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' })
    : '—';

function CardProgramacion() {
  const [schedules, setSchedules] = useState<Programacion[]>([]);
  const [frase, setFrase] = useState('');
  const [tipo, setTipo] = useState<'alerta' | 'boletin'>('boletin');
  const [vista, setVista] = useState<{ ok: boolean; texto: string } | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fraseId = useId();
  const tipoId = useId();

  const cargar = useCallback(async () => {
    try {
      const data = await apiFetch<{ schedules: Programacion[] }>('/api/schedules');
      setSchedules(data.schedules);
    } catch (err) {
      setError(traducirError(err));
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Interpretación en vivo mientras se escribe (debounce): la frase nunca se adivina.
  useEffect(() => {
    if (!frase.trim()) { setVista(null); return; }
    const t = setTimeout(() => {
      void apiFetch<{ ok: boolean; interpretacion?: string; message?: string }>('/api/schedules/interpretar', {
        method: 'POST',
        body: JSON.stringify({ frase }),
      })
        .then((r) => setVista(r.ok ? { ok: true, texto: `Víctor correrá: ${r.interpretacion}` } : { ok: false, texto: r.message ?? 'Frase no entendida.' }))
        .catch(() => setVista(null));
    }, 450);
    return () => clearTimeout(t);
  }, [frase]);

  async function agregar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (guardando || !frase.trim()) return;
    setGuardando(true);
    setError(null);
    try {
      await apiFetch('/api/schedules', { method: 'POST', body: JSON.stringify({ frase, tipo }) });
      setFrase('');
      setVista(null);
      await cargar();
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function alternar(s: Programacion) {
    try {
      await apiFetch(`/api/schedules/${s.id}`, { method: 'PATCH', body: JSON.stringify({ enabled: !s.enabled }) });
      await cargar();
    } catch (err) {
      setError(traducirError(err));
    }
  }

  async function eliminar(id: string) {
    try {
      await apiFetch(`/api/schedules/${id}`, { method: 'DELETE' });
      await cargar();
    } catch (err) {
      setError(traducirError(err));
    }
  }

  return (
    <section className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-lg font-light text-azul-profundo">Programación de Víctor</h3>
        <p className="mt-1 text-sm font-light text-gris">
          Escribe cuándo debe correr en lenguaje natural — p. ej. "cada día a las 7am" o
          "lunes, miércoles y viernes a las 8:30". Siempre verás la interpretación antes de guardar.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <form onSubmit={agregar} className="flex flex-col gap-3">
        <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
          <div>
            <label htmlFor={fraseId} className={labelClass}>Cuándo</label>
            <input
              id={fraseId}
              type="text"
              value={frase}
              onChange={(e) => setFrase(e.target.value)}
              placeholder="cada día a las 7am"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={tipoId} className={labelClass}>Qué genera</label>
            <select id={tipoId} value={tipo} onChange={(e) => setTipo(e.target.value as 'alerta' | 'boletin')} className={inputClass}>
              <option value="boletin">Boletín</option>
              <option value="alerta">Alerta</option>
            </select>
            {tipo === 'boletin' && /cada d[ií]a|todos los d[ií]as|diari[ao]/i.test(frase) && (
              <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-normal text-amber-800 ring-1 ring-inset ring-amber-200">
                Un boletín DIARIO repetiría la semana anterior todos los días. Para el resumen
                del día, elige <span className="font-medium">Alerta</span> (error real del 13-jul:
                el cron diario quedó como boletín por este default).
              </p>
            )}
          </div>
        </div>
        {vista && (
          <p className={`text-xs font-normal ${vista.ok ? 'text-emerald-700' : 'text-amber-700'}`} aria-live="polite">
            {vista.ok ? '✓ ' : '✗ '}{vista.texto}
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={guardando || !vista?.ok}
            className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {guardando ? 'Guardando…' : 'Programar'}
          </button>
        </div>
      </form>

      {schedules.length > 0 && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {schedules.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-normal text-texto">
                  “{s.descripcion}” <span className="text-xs text-gris">→ {s.interpretacion}</span>
                </p>
                <p className="text-xs font-light text-gris">
                  {s.tipo === 'alerta' ? 'Alerta' : 'Boletín'} · próxima: {fmtProxima(s.next_run_at)}
                  {s.last_run_at ? ` · última: ${fmtProxima(s.last_run_at)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void alternar(s)}
                  className={`chip ${s.enabled ? 'bg-emerald-50 text-emerald-700 ring-emerald-300' : 'bg-slate-100 text-gris ring-slate-300'}`}
                >
                  {s.enabled ? 'Activa' : 'Pausada'}
                </button>
                <button
                  type="button"
                  onClick={() => void eliminar(s.id)}
                  className="text-xs font-medium text-red-600 hover:underline"
                >
                  Eliminar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ──────────────────────── Destinatarios de prueba ──────────────────────────

function CardDestinatariosPrueba({ inicial, modoInicial }: { inicial: string[]; modoInicial: boolean }) {
  const [emails, setEmails] = useState<string[]>(inicial);
  const [nuevo, setNuevo] = useState('');
  const [modo, setModo] = useState(modoInicial);
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nuevoId = useId();

  async function guardar(lista: string[], modoPrueba: boolean) {
    setGuardando(true);
    setError(null);
    setOk(false);
    try {
      await guardarAjuste('test_recipients', lista);
      await guardarAjuste('modo_prueba', modoPrueba);
      setOk(true);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setGuardando(false);
    }
  }

  function agregar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = nuevo.trim().toLowerCase();
    if (!email || emails.includes(email)) return;
    const lista = [...emails, email];
    setEmails(lista);
    setNuevo('');
    void guardar(lista, modo);
  }

  function quitar(email: string) {
    const lista = emails.filter((e) => e !== email);
    setEmails(lista);
    void guardar(lista, modo);
  }

  function alternarModo() {
    const nuevoModo = !modo;
    setModo(nuevoModo);
    void guardar(emails, nuevoModo);
  }

  return (
    <section className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-light text-azul-profundo">Destinatarios de prueba</h3>
          <p className="mt-1 text-sm font-light text-gris">
            Mientras el modo prueba esté activo, los envíos de las corridas programadas van
            SOLO a estos correos (asunto con [PRUEBA]); los clientes reales no reciben nada.
          </p>
        </div>
        <button
          type="button"
          onClick={alternarModo}
          disabled={guardando}
          className={`chip ${modo ? 'bg-amber-50 text-amber-700 ring-amber-300' : 'bg-emerald-50 text-emerald-700 ring-emerald-300'}`}
        >
          {modo ? 'Modo prueba ACTIVO' : 'Modo prueba apagado'}
        </button>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {ok && !error && <p className="text-xs font-normal text-emerald-700">Cambios guardados.</p>}

      <form onSubmit={agregar} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <label htmlFor={nuevoId} className={labelClass}>Agregar correo de prueba</label>
          <input
            id={nuevoId}
            type="email"
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            placeholder="tu-correo@ejemplo.cl"
            className={inputClass}
          />
        </div>
        <button
          type="submit"
          disabled={guardando || !nuevo.trim()}
          className="rounded-lg border border-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          Agregar
        </button>
      </form>

      {emails.length === 0 ? (
        <p className="text-sm font-light text-gris">Sin destinatarios de prueba aún.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {emails.map((email) => (
            <li key={email} className="chip bg-slate-100 text-slate-700 ring-slate-300">
              {email}
              <button type="button" onClick={() => quitar(email)} aria-label={`Quitar ${email}`} className="ml-1 text-gris hover:text-red-600">
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function SeccionAjustes() {
  const [settings, setSettings] = useState<NewsletterSettings | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await apiFetch<{ settings: NewsletterSettings }>('/api/settings');
      setSettings(data.settings);
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (cargando) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm font-light uppercase tracking-[0.2em] text-gris shadow-sm"
      >
        Cargando ajustes…
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
        <p className="text-sm text-red-700">{error ?? 'No se pudieron cargar los ajustes.'}</p>
        <button
          type="button"
          onClick={() => void cargar()}
          className="mt-4 rounded-lg border border-azul-profundo px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm font-light text-gris">
        Configura el aprobador, el remitente y los parámetros de envío del newsletter.
      </p>
      <CardProgramacion />
      <CardDestinatariosPrueba inicial={settings.test_recipients ?? []} modoInicial={settings.modo_prueba ?? true} />
      <FormAprobador inicial={settings.approver} />
      <FormRemitente inicial={settings.sender} />
      <FormTtl inicial={settings.approval_token_ttl_hours} />
      <SeccionFlags settings={settings} />
    </div>
  );
}
